// @flow
import React, { useEffect, useRef } from 'react';
import { map, filter, get, toLower, includes, size, omit, toNumber, sumBy } from 'lodash';
import {
  Modal, ClickableList, SearchInput,
  Button, Checkbox, InputMask, Loading,
  FormattedText,
  Icon,
  QuantityStepper,
} from '@elements';
import { PlatformSwitch } from '@blocks';
import type { ServicesState, FormElementData } from '@types';
import { sort, alphabeticCompare, createNsTranslator, handleSubmitEvent, keepKeysWithin } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import './ServicesNewListModal.scss';
import classNames from 'classnames';
import { OFFLINE_SEARCH_EDGE } from '@helpers/constants';
import ServiceService from '../../../../../services/ServiceService';

type Props = {
  onClose: Function,
  serviceTemplates: ServicesState,
  onSaveTemplates: Function,
  isLoading: boolean,
  onSetConfirmation: Function,
  onEditServices: Function,
  doctorName?: ?string,
  onCreateService: Function,
  servicesByQuantity: boolean,
  t: Function,
};

type State = {
  searchTerm: string,
  selectedServices: {
    [key: number | string]: boolean,
  },
  customPrices: {
    [key: number | string]: number,
  },
  quantities: {
    [key: number | string]: string,
  },
  invalidQuantities: {
    [key: number | string]: boolean,
  },
  missingQuantityOf: ?string,
  privateVisible: boolean,
  healthInsuranceVisible: boolean,
};


// The controls of a checked quantity row: Space in them (the unit price too, not only the stepper) would uncheck the row
const QuantityItemControls = ({ children, ...props }: { children: any }) => {
  const root = useRef(null);
  useEffect(() => keepKeysWithin(root.current, (event) => event.key === ' '), []);
  return <div ref={root} {...props}>{children}</div>;
};

const maptoList = (serviceTemplates, servicesByQuantity) => map(
  serviceTemplates,
  item => {
    const byQuantity = servicesByQuantity && ServiceService.isTemplateByQuantity(item);
    return {
      ...(byQuantity ? { className: 'listItem byQuantityItem' } : {}),
      customRendererData: {
        id: item.id,
        text: item.name,
        price: item.price,
        templateId: item.id,
        ...(byQuantity ? { unit: item.unit } : {}),
      },
    };
  },
);

const filterSearch = (serviceTemplates, searchTerm) => {
  const search = toLower(searchTerm).normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const searchPredicate = item => includes(toLower(item.name).normalize('NFD').replace(/[\u0300-\u036f]/g, ''), search);
  return search && String(search).trim().length > 0 ? filter(serviceTemplates, searchPredicate) : serviceTemplates;
};

const mapFromIdList = (serviceTemplates, idList) => filter(
  map(idList, id => get(serviceTemplates, String(id), null)),
  item => item !== null,
);

const servicesItems = (serviceTemplates, searchTerm, normalIds, healthInsuranceIds, _, privateVisible: boolean, healthInsuranceVisible: boolean, servicesByQuantity: boolean) => {
  let normalItems = mapFromIdList(serviceTemplates, normalIds);
  let healthInsuranceItems = mapFromIdList(serviceTemplates, healthInsuranceIds);

  normalItems = filterSearch(normalItems, searchTerm);
  healthInsuranceItems = filterSearch(healthInsuranceItems, searchTerm);

  normalItems = maptoList(normalItems, servicesByQuantity);
  healthInsuranceItems = maptoList(healthInsuranceItems, servicesByQuantity);

  normalItems = sort(normalItems, alphabeticCompare('text'));
  healthInsuranceItems = sort(healthInsuranceItems, alphabeticCompare('text'));

  return [
    ...(normalItems.length > 0 ? [{
      type: 'heading',
      private: true,
      text: _('label/private'),
    }] : []),
    ...(privateVisible ? normalItems || [] : []),
    ...(healthInsuranceItems.length > 0 ? [{
      type: 'heading',
      private: false,
      text: _('label/status_health_insurance'),
    }] : []),
    ...(healthInsuranceVisible ? healthInsuranceItems || [] : []),
  ];
};


class ServicesNewListModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedServices: {},
      customPrices: {},
      quantities: {},
      invalidQuantities: {},
      missingQuantityOf: null,
      searchTerm: '',
      privateVisible: true,
      healthInsuranceVisible: true,
    };
  }


  componentDidMount() {
    handleSubmitEvent('#servicesNewListModalSubmit', this.handleFormSave);
  }

  renderListItem = (item: Object, key: string) => {
    const { selectedServices, customPrices } = this.state;
    const isChecked = get(selectedServices, String(item.id), false) && !item.healthInsurance;
    const isActive = isChecked && !item.healthInsurance;
    const price = get(customPrices, String(item.id), item.price);

    if (item.unit) {
      return this.renderQuantityListItem(item, key, isChecked, price);
    }

    return [
      <Checkbox
        key={`chkbox-${key}`}
        name="price"
        label={item.text}
        isChecked={isChecked}
        onChange={this.handleItemSelectionChange(item.id, item.price)}
      />,
      <div className={`selectItem ${isActive && 'active'}`} key={`selectItem-${key}`}>
        <div className={`selectItemValueTransition  ${isActive ? 'visible' : 'invisible'}`}>
          {isActive && (
            <InputMask
              type="currency"
              value={price}
              onChange={this.handleItemCustomPriceChange(item.id)}
              onClick={this.onInputClick}
            />
          )}
        </div>
        <span className={`selectItemValueTransition  ${isActive ? 'invisible' : 'visible'}`}>
          <FormattedText type="currency" text={item.price} />
        </span>
      </div>,
    ];
  };

  renderQuantityListItem = (item: Object, key: string, isChecked: boolean, unitPrice: any) => {
    const { quantities, invalidQuantities } = this.state;
    const { t } = this.props;
    const _ = createNsTranslator('financial', t);
    const quantityText = get(quantities, String(item.id), '1');
    const quantity = ServiceService.parseQuantity(quantityText);
    const perUnit = t('financial:label/per_unit', { unit: item.unit });

    return [
      <div className="quantityItemName" key={`name-${key}`}>
        <Checkbox
          name="price"
          label={item.text}
          isChecked={isChecked}
          onChange={this.handleItemSelectionChange(item.id, item.price)}
        />
        <div className="quantityItemHint">
          {perUnit}
          {isChecked && (
            <FormattedText type="currency" text={item.price}>
              {(price) => ` · ${t('financial:label/table_price', { price })}`}
            </FormattedText>
          )}
        </div>
      </div>,
      <QuantityItemControls
        className={classNames('selectItem', 'quantityItemControls', { active: isChecked })}
        key={`selectItem-${key}`}
        onClick={isChecked ? this.onInputClick : undefined}
        role="presentation"
      >
        {isChecked ? (
          <React.Fragment>
            <QuantityStepper
              value={quantityText}
              invalid={get(invalidQuantities, String(item.id), false)}
              onChange={this.handleItemQuantityChange(item.id)}
              inputLabel={_('label/quantity', 'global')}
              decreaseLabel={_('action/decrease_quantity')}
              increaseLabel={_('action/increase_quantity')}
            />
            <span className="quantityItemUnit">{ServiceService.unitLabel(item.unit, quantity)} ×</span>
            <div className="quantityItemPrice">
              <InputMask
                type="currency"
                value={unitPrice}
                onChange={this.handleItemCustomPriceChange(item.id)}
              />
            </div>
            <span className="quantityItemTotal">
              =&nbsp;<FormattedText type="currency" text={ServiceService.lineTotal(quantity, unitPrice)} />
            </span>
          </React.Fragment>
        ) : (
          <FormattedText type="currency" text={item.price} />
        )}
      </QuantityItemControls>,
    ];
  };

  // In the desktop footer it stays even when empty, so the buttons don't move on the first check.
  // The total leaves out convênio services, as the patient's balance does.
  renderSelectionSummary = (keepWhenEmpty: boolean = false) => {
    const { servicesByQuantity, serviceTemplates, t } = this.props;
    const { missingQuantityOf } = this.state;
    if (!servicesByQuantity) return null;

    const selected = this.selectedItems();
    if (selected.length === 0) return keepWhenEmpty ? <div className="selectionSummary" /> : null;
    const charged = selected.filter(item => !get(serviceTemplates, ['data', String(item.templateId), 'healthInsurance'], false));

    return (
      <div className="selectionSummary">
        {missingQuantityOf ? (
          <span className="selectionError" role="alert">{t('financial:label/error/quantity_of', { name: missingQuantityOf })}</span>
        ) : (
          <span>{t('financial:label/selected_services', { count: selected.length })}</span>
        )}
        <span className="selectionTotal">
          {t('global:label/total')}&nbsp;<FormattedText type="currency" text={sumBy(charged, item => toNumber(item.price))} />
        </span>
      </div>
    );
  };

  render() {
    const {
      onClose, serviceTemplates, isLoading, onEditServices, doctorName, servicesByQuantity,
    } = this.props;
    const { searchTerm, privateVisible, healthInsuranceVisible } = this.state;
    const _ = createNsTranslator('financial', this.props.t);

    const modalTitle = doctorName ? `${_('label/new_service_to')} ${doctorName}` : _('action/new_service');

    const serviceTemplatesData = get(serviceTemplates, 'data', {});
    const normalIds = get(serviceTemplates, 'normal', {});
    const healthInsuranceIds = get(serviceTemplates, 'healthInsurance', {});
    const showSerch = size(serviceTemplatesData) > OFFLINE_SEARCH_EDGE;

    const headingRenderer = (item: any, key: string, classes: string) => (
      <li key={key} className={classNames(classes, 'checkableHeading')} onClick={item.private ? this.handleTogglePrivateVisibility : this.handleToggleHealthInsuranceVisibility}>
        <div className="textContent">
          {item.text}
        </div>
        <div className="checkboxHolder">
          {(item.private ? this.state.privateVisible : this.state.healthInsuranceVisible) && (
            <React.Fragment>{_('action/hide_list', 'global')}&nbsp;<Icon iconName="arrow-down" /></React.Fragment>
          )}

          {!(item.private ? this.state.privateVisible : this.state.healthInsuranceVisible) && (
            <React.Fragment>{_('action/show_list', 'global')}&nbsp;<Icon iconName="arrow-left" /></React.Fragment>
          )}
        </div>
      </li>
    );

    const content = (
      <div>
        <div className="form mainActionHolder">
          {showSerch && (
            <SearchInput
              debounced
              placeholder={_('action/search_service')}
              onChange={this.handleSearchChange}
            />
          )}
          <div className="addNewTemplateButton" onClick={this.handleCreateService}>
            <Icon iconName="add" />&nbsp;{_('action/create_new_receipt')}
          </div>
        </div>


        {isLoading && <Loading />}
        {!isLoading &&
          <ClickableList
            items={servicesItems(serviceTemplatesData, searchTerm, normalIds, healthInsuranceIds, _, privateVisible, healthInsuranceVisible, servicesByQuantity)}
            customRenderer={this.renderListItem}
            className="checkboxList"
            onListItemClick={this.handleItemSelectionClick}
            customHeadingRenderer={headingRenderer}
          />
        }
      </div>
    );

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <Modal id="ServicesNewListModal" onClose={onClose}>
            <div className="modalHeader">
              <span className="modalTitle">{modalTitle}</span>
            </div>

            <div className="modalGrid">
              <div className="modalContainer" id="servicesNewListModalSubmit">
                <div className="modalContent">
                  {content}
                </div>

                <div className="modalFooter">
                  {this.renderSelectionSummary(true)}
                  <Button text={_('action/cancel', 'global')} onClick={onClose} />
                  <Button text={_('action/edit_services')} onClick={onEditServices} />
                  <Button className="primary" text={_('action/save', 'global')} onClick={this.handleFormSave} />
                </div>
              </div>

            </div>
          </Modal>
        )}
        mobile={() => (
          <Modal id="ServicesNewListModal" className="asView" onClose={onClose}>
            <div className="modalHeader">
              <Button onClick={onClose}>
                <Icon iconName="close" className="icon" />
              </Button>
              <span className="title">{modalTitle}</span>
              <Button className="primary" text={_('action/save', 'global')} onClick={this.handleFormSave} />
            </div>
            <div className="modalGrid">
              <div className="modalContainer">
                <div className="modalContent">
                  {content}
                </div>
                {this.renderSelectionSummary()}
              </div>

            </div>
          </Modal>
        )}
      />
    );
  }

  handleSearchChange = (value: string) => this.setState({
    searchTerm: value || '',
  });

  handleItemSelectionClick = (item: Object) => {
    const isChecked = get(this.state.selectedServices, String(item.id), false);
    this.handleItemSelectionChange(item.id, item.price)({ value: !isChecked, name: '' });
  }

  onInputClick = (event: Object) => {
    event.stopPropagation();
  };

  handleItemSelectionChange = (id: number, price: number) => (data: Object) =>
    this.setState({
      selectedServices: {
        ...this.state.selectedServices,
        [id]: data.value,
      },
    },
    () => {
      if (!data.value) {
        this.handleItemCustomPriceChange(id)({ name: String(id), value: String(price) });
        this.setState(s => ({
          quantities: omit(s.quantities, String(id)),
          invalidQuantities: omit(s.invalidQuantities, String(id)),
          missingQuantityOf: null,
        }));
      }
      this.handleConfirmation();
    });

  handleItemQuantityChange = (id: number) => (value: string) => this.setState(s => ({
    quantities: { ...s.quantities, [id]: value },
    invalidQuantities: omit(s.invalidQuantities, String(id)),
    missingQuantityOf: null,
  }));

  handleItemCustomPriceChange = (id: number) => (data: FormElementData) => this.setState({
    customPrices: {
      ...this.state.customPrices,
      [id]: String(data.value).replace(/[^0-9.]/g, ''),
    },
  });

  handleConfirmation = () => this.props.onSetConfirmation(Object.keys(this.state.selectedServices).length > 0);

  // The services to create: { templateId, price }, plus quantity and unitPrice for a quantity template
  selectedItems = () => {
    const { selectedServices, customPrices, quantities } = this.state;
    const { serviceTemplates, servicesByQuantity } = this.props;

    return Object.keys(selectedServices)
      .filter(key => selectedServices[key] === true)
      .map(id => {
        const serviceTemplate = get(serviceTemplates, ['data', String(id)], {});
        const itemPrice = get(customPrices, String(id), get(serviceTemplate, 'price', 0));

        if (servicesByQuantity && ServiceService.isTemplateByQuantity(serviceTemplate)) {
          const quantity = ServiceService.parseQuantity(get(quantities, String(id), '1'));
          const unitPrice = toNumber(itemPrice);
          return {
            templateId: get(serviceTemplate, 'id', null),
            price: ServiceService.lineTotal(quantity, unitPrice),
            quantity,
            unitPrice,
          };
        }

        return serviceTemplate ? {
          templateId: get(serviceTemplate, 'id', null),
          price: itemPrice,
        } : null;
      })
      .filter(item => item !== null);
  };

  handleFormSave = () => {
    const services = this.selectedItems();
    const invalid = services.filter(service => service.quantity !== undefined && !ServiceService.isValidQuantity(service.quantity));

    if (invalid.length > 0) {
      // The row may be scrolled away or hidden by the search, so the footer names it too
      const invalidQuantities = {};
      invalid.forEach(service => { invalidQuantities[service.templateId] = true; });
      const missingQuantityOf = get(this.props.serviceTemplates, ['data', String(invalid[0].templateId), 'name'], '');
      this.setState({ invalidQuantities, missingQuantityOf }, this.focusInvalidQuantity);
      return;
    }

    this.props.onSaveTemplates(services);
  };

  focusInvalidQuantity = () => {
    const input = document.querySelector('#ServicesNewListModal .quantityStepper.invalid input');
    if (!input) return;
    if (input.scrollIntoView) input.scrollIntoView({ block: 'center' });
    input.focus();
  };

  handleCreateService = () => {
    this.props.onCreateService(this.state.searchTerm);
  };

  handleTogglePrivateVisibility = () => {
    this.setState(s => ({
      privateVisible: !s.privateVisible,
    }));
  };

  handleToggleHealthInsuranceVisibility = () => {
    this.setState(s => ({
      healthInsuranceVisible: !s.healthInsuranceVisible,
    }));
  };
}

export default withTranslation()(ServicesNewListModal);

